<template>
  <Page>
    <ManageScheduleTemplateItemModal
      v-if="isScheduleItemShowig"
      :scheduleItem="getScheduleItem()"
      :createScheduleType="createScheduleType"
      :onClose="onCloseScheduleItem"
      :onUpdated="onUpdatedSchedule" />
    <ConfirmModal
      v-if="isConfirmModalShowing"
      :onClose="handleModal"
      :onConfirm="onDelete">
      <template v-slot:header-title>
        <h4>ยืนยันการลบ</h4>
      </template>
      <div>คุณยืนยันที่จะลบเทมเพลต {{getScheduleTemplateName()}}</div>
    </ConfirmModal>
    <template v-slot:page-header>
      <div class="container">
        <div class="page-header">
          <router-link
            class="btn btn-action btn-action-back"
            :to="`/schedule-templates`">
            <i class="fas fa-chevron-left" />
          </router-link>
          <div class="title-container">
            <div class="subtitle">แก้ไขเทมเพลต</div>
            <div class="title">{{getScheduleTemplateName()}}</div>
          </div>
          <div class="actions">
            <small class="text-danger mr-3"
              v-if="isServerError()">
              พบข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองอีกครั้งหรือติดต่อผู้ดูแลระบบ code : {{errorStatus}}
            </small>
            <small class="text-success font-weight-bold mr-3" v-if="saveScheduleTemplateState.isSaved">
              <i class="fa fa-check" /> บันทึกแล้ว
            </small>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="!isValid"
              @click.stop="onSubmit">
              บันทึก
            </button>
            <Dropdown :alignment="'right'">
              <template v-slot:dropdow-button>
                <button
                  type="button"
                  class="btn btn-outline-dark btn-action m-0 ml-2">
                  <i class="fas fa-cog"></i>
                </button>
              </template>
              <div class="dropdown-item" @click.stop="handleModal">
                ลบเทมเพลต
              </div>
            </Dropdown>
          </div>
        </div>
      </div>
    </template>
    <div class="container">
      <div class="row">
        <div class="col-md-6">
          <FormGroup :errorMessage="saveScheduleTemplateState.error.name">
            <h5 class="name">ชื่อเทมเพลต</h5>
            <input
              type="text"
              placeholder="ชื่อเทมเพลต"
              class="form-control"
              :class="{'is-invalid': saveScheduleTemplateState.error.name}"
              v-model="scheduleTemplate.name"
              @blur="checkRequired" />
            <div
              class="invalid-feedback"
              v-if="saveScheduleTemplateState.error.name">ระบุชื่อของเทมเพลต</div>
          </FormGroup>
        </div>
        <div class="col-md-12">
          <div class="section-container">
            <Section>
              <template v-slot:section-header>
                <h1 class="title">รายการ</h1>
                <div class="action">
                  <Dropdown :alignment="'right'">
                    <template v-slot:dropdow-button>
                      <button
                        type="button"
                        class="btn btn-light">
                        สร้าง
                      </button>
                    </template>
                    <div class="dropdown-item"
                      @click.stop="onCreateScheduleItem('newSchedule')">
                      สร้างใหม่
                    </div>
                    <div class="dropdown-item"
                      @click.stop="onCreateScheduleItem('existingProgram')">
                      เลือกจากรายการและตอนที่มีอยู่แล้ว
                    </div>
                  </Dropdown>
                </div>
              </template>
              <template v-slot:section-body>
                <div v-if="!isEmpty()">
                  <List>
                    <template v-slot:list-header>
                      <div class="list-item-image"></div>
                      <div class="list-item-detail">
                        <div
                          class="list-item list-item-primary list-item-title">
                          ชื่อรายการ
                        </div>
                        <div class="list-item list-item-secondary">
                          วันออนแอร์
                        </div>
                        <div class="list-item list-item-option">
                          เวลาออนแอร์
                        </div>
                        <div class="list-item list-item-action"></div>
                      </div>
                    </template>
                    <template v-slot:list-body>
                      <div class="list-item-container no-underline"
                        v-for="(item, itemIndex) in scheduleTemplate.items"
                        :key="itemIndex">
                        <div class="list-item-image">
                          <ImageView
                            :source="item.coverImage" />
                        </div>
                        <div class="list-item-detail">
                          <div class="list-item list-item-primary list-item-title">
                            <div class="title">{{item.programTitle}}</div>
                            <div class="subtitle">
                              {{item.episodeTitle}}
                            </div>
                            <div class="schedule-option-container">
                              <ScheduleItemOption
                                :isRerun="item.isRerun"
                                :features="item.features"
                                :broadcastingChannels="item.broadcastingChannels" />
                            </div>
                          </div>
                          <div class="list-item list-item-secondary">
                            <ScheduleTemplateItemOnAirDate
                              :recurrentDaysOfWeek="item.recurrentDaysOfWeek" />
                          </div>
                          <div class="list-item list-item-option">
                            {{formatScheduleTimeWithoutTimezone(item.startTime)}} - {{formatScheduleTimeWithoutTimezone(item.endTime)}}
                          </div>
                          <div class="list-item list-item-action">
                            <div class="action-container">
                              <button
                                class="btn btn-light btn-action"
                                @click="onRemove(itemIndex)">
                                <i class="far fa-trash-alt"></i>
                              </button>
                              <button
                                class="btn btn-light btn-action"
                                @click="onSelect(itemIndex)">
                                <i class="fas fa-pencil-alt"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </List>
                </div>
                <EmptyMessage v-if="isEmpty()">
                  <h3 class="title">ไม่มีข้อมูล</h3>
                  <p>กรุณาสร้างรายการ</p>
                </EmptyMessage>
              </template>
            </Section>
          </div>
        </div>
      </div>
    </div>
  </Page>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
import dateFormetter from '@/utils/dateFormatter';
import scheduleManagement from '@/utils/scheduleManagement';
import Dropdown from '@/components/Dropdown.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import FormGroup from '@/components/FormGroup.vue';
import Section from '@/components/Section.vue';
import List from '@/components/List.vue';
import Page from '@/layouts/Page.vue';
import EmptyMessage from '@/components/EmptyMessage.vue';
import ImageView from '@/image/ImageView.vue';
import ScheduleItemOption from '@/schedule/scheduleItem/ScheduleItemOption.vue';

import {
  updateScheduleTemplateById,
  deleteScheduleTemplate,
} from './api';
import ScheduleTemplateItemOnAirDate from './ScheduleTemplateItemOnAirDate.vue';
import ManageScheduleTemplateItemModal from './ManageScheduleTemplateItemModal.vue';

export default {
  mixins: [dateFormetter, scheduleManagement],
  props: {
    scheduleTemplateData: {
      type: Object,
      default: () => {},
    },
  },
  components: {
    Page,
    Dropdown,
    ConfirmModal,
    ImageView,
    FormGroup,
    Section,
    List,
    EmptyMessage,
    ScheduleItemOption,
    ScheduleTemplateItemOnAirDate,
    ManageScheduleTemplateItemModal,
  },
  data() {
    return {
      scheduleTemplate: null,
      currentScheduleTemplate: null,
      isConfirmModalShowing: false,
      selectedScheduleItemIndex: null,
      createScheduleType: 'newSchedule', // existingProgram
      isScheduleItemShowig: false,
      errorStatus: null,
      saveScheduleTemplateState: {
        isSaving: false,
        isSaved: false,
        error: { // required field
          name: null,
        },
      },
    };
  },
  created() {
    this.scheduleTemplate = _.cloneDeep(this.scheduleTemplateData);
    this.currentScheduleTemplate = _.cloneDeep(this.scheduleTemplateData);
  },
  computed: {
    scheduleTemplateId() {
      return this.$route.params.scheduleTemplateId;
    },
    isScheduleTemplateValid() {
      return this.scheduleTemplate.name;
    },
    haveErrorMessage() {
      return _.some(this.saveScheduleTemplateState.error, (value) => value);
    },
    isValid() {
      return this.isScheduleTemplateValid && !this.haveErrorMessage;
    },
  },
  methods: {
    isEmpty() {
      return _.isEmpty(this.scheduleTemplate.items);
    },
    async onSubmit() {
      this.saveScheduleTemplateState.isSaving = true;
      this.saveScheduleTemplateState.isSaved = false;
      try {
        const response = await updateScheduleTemplateById(this.scheduleTemplateId, this.scheduleTemplate);
        this.scheduleTemplate = response.data;
        this.currentScheduleTemplate = _.cloneDeep(response.data);
        this.saveScheduleTemplateState.isSaving = false;
        this.saveScheduleTemplateState.isSaved = true;
        this.errorStatus = null;
      } catch (error) {
        this.errorStatus = error.response.status;
        this.saveScheduleTemplateState.error = error.response.data;
        this.saveScheduleTemplateState.isSaving = false;
      }
    },
    async onDelete() {
      try {
        await deleteScheduleTemplate(this.scheduleTemplateId);
        this.$router.push('/schedule-templates');
      } catch (error) {
        this.errorStatus = error.response.status;
        this.saveScheduleTemplateState.error = error.response.data;
      }
    },
    sortScheduleItemsByStartTime(scheduleItems) {
      return _.sortBy(scheduleItems, 'startTime');
    },
    onUpdatedSchedule(scheduleItem) {
      if (!_.isNull(this.selectedScheduleItemIndex)) {
        Vue.set(this.scheduleTemplate.items, this.selectedScheduleItemIndex, scheduleItem);
        this.selectedScheduleItemIndex = null;
      } else {
        this.scheduleTemplate.items = [_.cloneDeep(scheduleItem), ...this.scheduleTemplate.items];
      }

      // re-sort
      this.scheduleTemplate.items = this.sortScheduleItemsByStartTime(this.scheduleTemplate.items);

      // on close modal
      this.onCloseScheduleItem();
    },
    onCloseScheduleItem() {
      this.createScheduleType = 'newSchedule';
      this.selectedScheduleItemIndex = null;
      this.isScheduleItemShowig = false;
    },
    onToggleScheduleItem() {
      this.isScheduleItemShowig = !this.isScheduleItemShowig;
    },
    getScheduleItem() {
      const scheduleItem = _.get(this.scheduleTemplate.items, this.selectedScheduleItemIndex, null);
      if (!scheduleItem) {
        return {};
      }
      return scheduleItem;
    },
    onCreateScheduleItem(creatType) {
      this.createScheduleType = creatType;
      this.onToggleScheduleItem();
    },
    onSelect(itemIndex) {
      this.selectedScheduleItemIndex = itemIndex;
      this.onToggleScheduleItem();
    },
    onRemove(itemIndex) {
      this.scheduleTemplate.items = _.remove(this.scheduleTemplate.items, (item, index) => index !== itemIndex);
    },
    handleModal() {
      this.isConfirmModalShowing = !this.isConfirmModalShowing;
    },
    isServerError() {
      return this.errorStatus;
    },
    getScheduleTemplateName() {
      return _.get(this.currentScheduleTemplate, 'name', this.scheduleTemplateId);
    },
    checkRequired(event) {
      if (!event.target.value) {
        this.saveScheduleTemplateState.error = {
          ...this.saveScheduleTemplateState.error,
          name: 'ระบุชื่อเทมเพลต',
        };
      } else {
        this.saveScheduleTemplateState.error = {
          ...this.saveScheduleTemplateState.error,
          name: null,
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables.scss';
@import './src/assets/scss/themes/components/section-container.scss';
</style>
